import { Platform, StyleSheet } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'

export const landingGetTheAppGradientStart = { x: 0, y: 0 }
export const landingGetTheAppGradientEnd = { x: 1, y: 1 }

export function getLandingGetTheAppStyles(theme: Theme) {
  return StyleSheet.create({
    section: {
      paddingHorizontal: 24,
      paddingVertical: 48,
    },
    sectionPhone: {
      paddingHorizontal: 14,
      paddingVertical: 28,
    },
    container: {
      maxWidth: 1720,
      marginHorizontal: 'auto' as unknown as number,
      width: '100%',
    },
    card: {
      borderRadius: 28,
      overflow: 'hidden',
      position: 'relative',
      paddingVertical: 64,
      paddingHorizontal: 48,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 40,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 30 },
      shadowOpacity: 0.18,
      shadowRadius: 60,
      elevation: 8,
    },
    cardStacked: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 32,
      paddingVertical: 48,
      paddingHorizontal: 32,
    },
    cardPhone: {
      borderRadius: 24,
      paddingVertical: 28,
      paddingHorizontal: 20,
      gap: 0,
    },
    gradientFill: {
      ...StyleSheet.absoluteFillObject,
    },
    accentBlob: {
      position: 'absolute',
      top: -300,
      right: -120,
      width: 600,
      height: 600,
      borderRadius: 600,
      backgroundColor: theme.accent + '2E',
    },
    accentBlobPhone: {
      top: -180,
      right: -160,
      width: 360,
      height: 360,
      borderRadius: 360,
    },

    /* Desktop side-by-side columns */
    leftCol: {
      flex: 1.1,
      minWidth: 0,
    },
    leftColStacked: {
      flexBasis: 'auto',
      flexGrow: 0,
      flexShrink: 0,
      width: '100%',
    },
    rightCol: {
      flex: 0.9,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
    },
    rightColStacked: {
      flexBasis: 'auto',
      flexGrow: 0,
      flexShrink: 0,
      width: '100%',
    },

    eyebrow: {
      fontFamily:
        Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.4,
      textTransform: 'uppercase',
      color: 'rgba(255, 255, 255, 0.78)',
      marginBottom: 14,
    },
    h2: {
      fontFamily:
        Platform.OS === 'web'
          ? '"Space Grotesk", "Inter", system-ui, sans-serif'
          : undefined,
      fontSize: 40,
      fontWeight: '700',
      lineHeight: 46,
      letterSpacing: -1,
      color: theme.white,
      marginBottom: 14,
    },
    h2Stacked: {
      fontSize: 34,
      lineHeight: 40,
    },
    p: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 17,
      lineHeight: 26,
      color: 'rgba(255, 255, 255, 0.85)',
      maxWidth: 480,
    },
    actions: {
      marginTop: 24,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    btnBase: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      borderRadius: 12,
      paddingHorizontal: 20,
      minHeight: 52,
      paddingVertical: 12,
    },
    btnSecondary: {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.32)',
    },
    btnSecondaryHover: {
      borderColor: theme.white,
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    btnSecondaryPressed: {
      backgroundColor: 'rgba(255, 255, 255, 0.18)',
    },
    btnSecondaryText: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 16,
      fontWeight: '600',
      color: theme.white,
    },

    /* Desktop QR card (white pill) */
    qrCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.97)',
      borderRadius: 20,
      padding: 24,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 24 },
      shadowOpacity: 0.32,
      shadowRadius: 50,
      elevation: 10,
      maxWidth: 340,
      width: '100%',
    },
    qrFrame: {
      width: 120,
      height: 120,
      backgroundColor: theme.white,
      borderRadius: 10,
      padding: 8,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    qrText: {
      flexShrink: 1,
      minWidth: 0,
    },
    qrTitle: {
      fontFamily:
        Platform.OS === 'web'
          ? '"Space Grotesk", "Inter", system-ui, sans-serif'
          : undefined,
      fontSize: 18,
      fontWeight: '700',
      letterSpacing: -0.2,
      color: theme.onPrimary,
      marginBottom: 4,
    },
    qrSubtitle: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 13,
      color: theme.gray[400],
    },
    storePill: {
      alignSelf: 'flex-start',
      marginTop: 10,
      paddingVertical: 4,
      paddingHorizontal: 10,
      borderRadius: 999,
      backgroundColor: theme.primary + '14',
    },
    storePillText: {
      fontFamily:
        Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
      fontSize: 11,
      fontWeight: '600',
      letterSpacing: 0.4,
      color: theme.primaryDark,
      textTransform: 'uppercase',
    },

    /* PHONE: rebuilt as a single vertical column. NO row/column siblings,
       NO flex numbers — just sequential blocks centered in the card. */
    phoneStack: {
      width: '100%',
      alignItems: 'center',
      gap: 0,
    },
    phoneEyebrow: {
      fontFamily:
        Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 1.4,
      textTransform: 'uppercase',
      color: 'rgba(255, 255, 255, 0.78)',
      textAlign: 'center',
      marginBottom: 10,
    },
    phoneTitle: {
      fontFamily:
        Platform.OS === 'web'
          ? '"Space Grotesk", "Inter", system-ui, sans-serif'
          : undefined,
      fontSize: 22,
      fontWeight: '700',
      lineHeight: 28,
      letterSpacing: -0.4,
      color: theme.white,
      textAlign: 'center',
      marginBottom: 8,
    },
    phoneBody: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 14,
      lineHeight: 21,
      color: 'rgba(255, 255, 255, 0.82)',
      textAlign: 'center',
      maxWidth: 320,
      marginBottom: 20,
    },
    phoneActions: {
      width: '100%',
      flexDirection: 'column',
      gap: 10,
      marginBottom: 22,
    },
    phoneBtn: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      borderRadius: 14,
      paddingHorizontal: 16,
      minHeight: 48,
      paddingVertical: 12,
    },
    phoneBtnText: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 15,
      fontWeight: '700',
    },
    /** Phone QR ribbon — small, secondary, centered footer of the card. */
    phoneQrRibbon: {
      width: '100%',
      maxWidth: 320,
      alignSelf: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.10)',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.18)',
      borderRadius: 16,
      paddingVertical: 12,
      paddingHorizontal: 14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    phoneQrFrame: {
      width: 64,
      height: 64,
      backgroundColor: theme.white,
      borderRadius: 8,
      padding: 5,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    phoneQrTextWrap: {
      flex: 1,
      minWidth: 0,
    },
    phoneQrTitle: {
      fontFamily:
        Platform.OS === 'web'
          ? '"Space Grotesk", "Inter", system-ui, sans-serif'
          : undefined,
      fontSize: 13,
      fontWeight: '700',
      color: theme.white,
      letterSpacing: -0.1,
      marginBottom: 2,
    },
    phoneQrSubtitle: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 11,
      color: 'rgba(255, 255, 255, 0.7)',
      marginBottom: 4,
    },
    phoneStorePill: {
      alignSelf: 'flex-start',
      paddingVertical: 2,
      paddingHorizontal: 8,
      borderRadius: 999,
      backgroundColor: theme.accent + '33',
      borderWidth: 1,
      borderColor: theme.accent + '55',
    },
    phoneStorePillText: {
      fontFamily:
        Platform.OS === 'web' ? '"JetBrains Mono", ui-monospace, monospace' : undefined,
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 0.5,
      color: theme.accentLight,
      textTransform: 'uppercase',
    },
  })
}
